/**
 * PartitionPopovers — Designer-side popover wrappers for the Slice E1
 * partition-scheme / add-partition / drop-partition forms.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice E1.
 */
import { useState } from 'react';
import { Plus, Settings2, Unplug } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/ui/popover';
import type { QualifiedName } from '../../../../sql-grammar/model';
import {
  getAddPartitionUnsupportedTooltip,
  isAddPartitionSupported,
} from '../../edit/edits/addPartition';
import {
  getDropPartitionUnsupportedTooltip,
  isDropPartitionSupported,
} from '../../edit/edits/dropPartition';
import {
  getSetPartitionSchemeUnsupportedTooltip,
  isSetPartitionSchemeSupported,
} from '../../edit/edits/setPartitionScheme';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { AddPartitionFormBody } from './AddPartitionFormBody';
import { DisabledAffordance } from './DisabledAffordance';
import { DropPartitionFormBody } from './DropPartitionFormBody';
import { PartitionSchemeFormBody } from './PartitionSchemeFormBody';

export interface PartitionPopoverProps {
  readonly editContext: VqeDdlEditContext;
  readonly table: QualifiedName | null;
  readonly testId?: string;
  readonly onCommitted?: () => void;
}

export function SetPartitionSchemePopover(props: PartitionPopoverProps) {
  const { editContext, table, testId = 'partition-scheme-popover', onCommitted } = props;
  const engine = editContext.service.engineId;
  const [open, setOpen] = useState(false);

  if (!isSetPartitionSchemeSupported(engine)) {
    return (
      <DisabledAffordance
        testId={`${testId}-trigger`}
        tooltip={getSetPartitionSchemeUnsupportedTooltip(engine)}
        variant="ghost"
        ariaLabel="Set partitioning scheme"
      >
        <Settings2 className="h-3.5 w-3.5" />
        Set scheme…
      </DisabledAffordance>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          data-testid={`${testId}-trigger`}
          aria-label="Set partitioning scheme"
        >
          <Settings2 className="h-3.5 w-3.5" />
          Set scheme…
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={16}
        className="max-h-[min(480px,var(--radix-popover-content-available-height))] w-80 overflow-y-auto"
        data-testid={testId}
      >
        {open ? (
          <PartitionSchemeFormBody
            editContext={editContext}
            testId={testId}
            table={table}
            onCommitted={() => { setOpen(false); onCommitted?.(); }}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

export function AddPartitionPopover(props: PartitionPopoverProps) {
  const { editContext, table, testId = 'add-partition-popover', onCommitted } = props;
  const engine = editContext.service.engineId;
  const [open, setOpen] = useState(false);

  if (!isAddPartitionSupported(engine)) {
    return (
      <DisabledAffordance
        testId={`${testId}-trigger`}
        tooltip={getAddPartitionUnsupportedTooltip(engine)}
        variant="ghost"
      >
        <Plus className="h-3.5 w-3.5" />
        Partition
      </DisabledAffordance>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          data-testid={`${testId}-trigger`}
          aria-label="Add partition"
        >
          <Plus className="h-3.5 w-3.5" />
          Partition
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={16}
        className="max-h-[min(480px,var(--radix-popover-content-available-height))] w-80 overflow-y-auto"
        data-testid={testId}
      >
        {open ? (
          <AddPartitionFormBody
            editContext={editContext}
            testId={testId}
            table={table}
            onCommitted={() => { setOpen(false); onCommitted?.(); }}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

export function DropPartitionPopover(props: PartitionPopoverProps) {
  const { editContext, table, testId = 'drop-partition-popover', onCommitted } = props;
  const engine = editContext.service.engineId;
  const [open, setOpen] = useState(false);

  if (!isDropPartitionSupported(engine)) {
    return (
      <DisabledAffordance
        testId={`${testId}-trigger`}
        tooltip={getDropPartitionUnsupportedTooltip(engine)}
        variant="ghost"
      >
        <Unplug className="h-3.5 w-3.5" />
        Detach
      </DisabledAffordance>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          data-testid={`${testId}-trigger`}
          aria-label="Drop partition"
        >
          <Unplug className="h-3.5 w-3.5" />
          {engine === 'postgres' ? 'Detach' : 'Drop'}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={16}
        className="max-h-[min(480px,var(--radix-popover-content-available-height))] w-80 overflow-y-auto"
        data-testid={testId}
      >
        {open ? (
          <DropPartitionFormBody
            editContext={editContext}
            testId={testId}
            table={table}
            onCommitted={() => { setOpen(false); onCommitted?.(); }}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
